import { useState } from "react";
import { ArrowRight, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);
const Input = ({ testid, ...props }) => <input data-testid={testid} className="brutalist-input" {...props} />;
const TextArea = ({ testid, ...props }) => <textarea data-testid={testid} className="brutalist-input min-h-[70px] resize-y" {...props} />;
const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>
);

export default function MinorWorksStep1({ formData, updateForm, onNext }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const set = (field) => (e) => updateForm({ [field]: e.target.value });

  const handleNext = () => {
    if (!formData.client_name.trim()) { toast.error("Please enter the client name"); return; }
    if (!formData.description_of_works.trim()) { toast.error("Please describe the minor works"); return; }
    if (!formData.installation_address.trim()) { toast.error("Please enter the installation address"); return; }
    onNext();
  };

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="mw-step-1">
      <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Minor Electrical Installation Works Certificate
      </h2>
      <p className="text-xs text-[#71717A] mb-6">
        To be used only for minor electrical work which does not include the provision of a new circuit
      </p>

      {/* Part 1: Details of Minor Works */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Part 1: Details of the Minor Works</SectionTitle>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="form-field">
              <Label>Client name</Label>
              <Input testid="mw-client-name" value={formData.client_name} onChange={set("client_name")} placeholder="Client name" />
            </div>
            <div className="form-field">
              <Label>Client email (optional)</Label>
              <Input testid="mw-client-email" type="email" value={formData.client_email} onChange={set("client_email")} placeholder="client@email.com" />
            </div>
          </div>
          <div className="form-field">
            <Label>Description of minor works</Label>
            <TextArea testid="mw-description" value={formData.description_of_works} onChange={set("description_of_works")} placeholder="e.g. Additional socket outlet to kitchen ring" rows={2} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-3">
              <Label>Installation address</Label>
              <Input testid="mw-address" value={formData.installation_address} onChange={set("installation_address")} placeholder="Full address" />
            </div>
            <div className="form-field">
              <Label>Postcode</Label>
              <Input testid="mw-postcode" value={formData.installation_postcode} onChange={set("installation_postcode")} placeholder="e.g. M1 1AE" />
            </div>
          </div>
          <div className="form-field">
            <Label>Details of any departures from BS 7671</Label>
            <Input testid="mw-departures" value={formData.departures} onChange={set("departures")} placeholder="N/A" />
          </div>
          <div className="form-field">
            <Label>Comments on existing installation (including defects)</Label>
            <TextArea testid="mw-comments" value={formData.comments_existing} onChange={set("comments_existing")} placeholder="N/A" rows={2} />
          </div>
          <div className="form-field">
            <Label>Risk assessment attached?</Label>
            <select data-testid="mw-risk" value={formData.risk_assessment} onChange={set("risk_assessment")} className="brutalist-input">
              <option value="N/A">N/A</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>
      </div>

      {/* Part 2: Earthing & Bonding */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Part 2: Earthing and Bonding Arrangements (Reg. 132.16)</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="form-field">
            <Label>System earthing arrangement</Label>
            <select data-testid="mw-earthing" value={formData.system_earthing} onChange={set("system_earthing")} className="brutalist-input">
              <option value="">Select...</option>
              <option value="TN-S">TN-S</option>
              <option value="TN-C-S">TN-C-S</option>
              <option value="TT">TT</option>
              <option value="TN-C-S (PNB)">TN-C-S (PNB)</option>
            </select>
          </div>
          <div className="form-field">
            <Label>Zdb at distribution board (Ohm)</Label>
            <Input testid="mw-zdb" value={formData.zdb} onChange={set("zdb")} />
          </div>
          <div className="form-field">
            <Label>Adequate main protective conductors</Label>
            <select data-testid="mw-adequate" value={formData.adequate_main_conductors} onChange={set("adequate_main_conductors")} className="brutalist-input">
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="N/A">N/A</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <Label>Main protective bonding conductors to</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
            {[
              { field: "bonding_water", label: "Water" },
              { field: "bonding_gas", label: "Gas" },
              { field: "bonding_oil", label: "Oil" },
              { field: "bonding_structural", label: "Structural steel" },
            ].map((b) => (
              <div key={b.field}>
                <label className="text-xs text-[#71717A] font-medium">{b.label}</label>
                <select value={formData[b.field]} onChange={set(b.field)} className="brutalist-input mt-0.5">
                  <option value="N/A">N/A</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            ))}
            <div className="form-field">
              <label className="text-xs text-[#71717A] font-medium">Other</label>
              <Input testid="mw-bond-other" value={formData.bonding_other} onChange={set("bonding_other")} placeholder="Specify" />
            </div>
            <div className="form-field">
              <label className="text-xs text-[#71717A] font-medium">Earthing conductor</label>
              <Input testid="mw-earth-conductor" value={formData.earthing_conductor} onChange={set("earthing_conductor")} />
            </div>
          </div>
        </div>
      </div>

      {/* Declaration date */}
      <div className="mb-6">
        <Label>Certificate date</Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button data-testid="mw-cert-date" className="brutalist-input flex items-center justify-between cursor-pointer" type="button">
              <span className={formData.cert_date ? "text-[#0A0A0A]" : "text-[#9CA3AF]"}>
                {formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : "Select date"}
              </span>
              <CalendarIcon className="w-4 h-4 text-[#71717A]" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 border-2 border-[#0A0A0A] rounded-none" align="start">
            <Calendar mode="single" selected={formData.cert_date} onSelect={(d) => { updateForm({ cert_date: d }); setCalendarOpen(false); }} />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex justify-end">
        <button data-testid="mw-step-1-next" onClick={handleNext} className="brutalist-btn flex items-center gap-2">
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
